import AbstractCharacteristicValue from '@/domains/Abstract/AbstractCharacteristicValue';
import ProductCharacteristic from "@/domains/Products/ProductCharacteristic";
import Characteristic               from '@/domains/Characteristics/Characteristic';
import Utils                        from '@/core/utils/Utils';
import NumericRange                 from '@/core/types/NumericRange';
import CharacteristicAllowedValue   from '@/domains/Characteristics/CharacteristicAllowedValue';
import type Product from "@/domains/Products/Product";
import AbstractRange from "@/core/types/AbstractRange";
import Core from "@/core/Core";

/**
 * Значение характеристики у оффера. Конкретное значение под рамку продукта —
 * numeric как одно число, listValue как массив выбранных значений.
 */
export default class OfferCharacteristic extends AbstractCharacteristicValue {
  numericValue: number | null = null;

  static fromServer(data: any, offerCharacteristic = new OfferCharacteristic()): OfferCharacteristic {
    super.fromServer(data, offerCharacteristic);
    return offerCharacteristic;
  }

  getNumericValue(): number | null {
    return this.numericValue;
  }

  setNumericValue(value: unknown): void {
    this.numericValue = value as number ?? null;
  }

  /**
   * У оффера selectableSingle — ровно одно значение. В listValue оно лежит
   * массивом: так его отдаёт сервер и так же устроена колонка list_values.
   * Наружу значение отдаётся объектом — его ждут VSelectV2, сравнение с
   * рамкой продукта и OfferCharacteristic::fromFrontData на бэке.
   *
   * У ProductCharacteristic при том же типе значение остаётся массивом
   * допустимых вариантов, поэтому переопределение здесь, а не в предке.
   */
  get value(): unknown {
    if (this.valueType === Characteristic.VALUE_TYPE_SELECTABLE_SINGLE) {
      const values = this.listValue ?? [];

      if (values.length === 0) {
        return null;
      }
      else {
        return values[0];
      }
    }
    else {
      return super.value;
    }
  }

  set value(val: unknown) {
    if (this.valueType === Characteristic.VALUE_TYPE_SELECTABLE_SINGLE) {
      if (val === null || val === undefined) {
        this.listValue = null;
      }
      else {
        this.listValue = [val as CharacteristicAllowedValue];
      }
    }
    else {
      super.value = val;
    }
  }

  getMismatchByProduct(product: Product): string | null {
    const productChar = product.getProductCharacteristicById(this.characteristic.id)

    // такое точно бывает, например из-за версионнности категории. Поставщик создал предложение с одним набором характеристик,
    // после этого из категории удалили поле, создали номенклатуру, и мы пытаемся найти productChar, которой уже нет
    if (productChar === null) return null;
    return this.getMismatch(productChar);
  }

  /**
   * Сравнивает предложение поставщика с требованием покупателя.
   * Возвращает строку с описанием расхождения либо null, если расхождения нет.
   */
  getMismatch(productCharacteristic: ProductCharacteristic): string | null {
    if (this.isReadOnly) return null;
    // пользователь не выбрал ещё значение. нечего сравнивать
    if (Core.utils.isNullOrUndefined(this.value) === true || ( this.value instanceof AbstractRange && this.value.isEmpty()) ) return null;

    switch (this.valueType) {
      case Characteristic.VALUE_TYPE_STRING:
      case Characteristic.VALUE_TYPE_LINK: {
        return this.getStringMismatch(productCharacteristic);
      }
      case Characteristic.VALUE_TYPE_NUMERIC: {
        return this.getNumericMismatch(productCharacteristic);
      }
      case Characteristic.VALUE_TYPE_SELECTABLE_SINGLE: {
        return this.getSelectableSingleMismatch(productCharacteristic);
      }
      case Characteristic.VALUE_TYPE_SELECTABLE_MULTI: {
        return this.getSelectableMultiMismatch(productCharacteristic);
      }
      case Characteristic.VALUE_TYPE_ENTITY_LINK: {
        return null;
      }
      case Characteristic.VALUE_TYPE_IMAGE: {
        return null;
      }
      default: {
        return null;
      }
    }
  }

  /** Расхождение для строковых типов (string, link) */
  getStringMismatch(productCharacteristic: ProductCharacteristic): string | null {
    /** Требуемое покупателем значение */
    const requiredValue = productCharacteristic.value;

    /** Требуется конкретная строка, а предложение с ней не совпадает — расхождение */
    if (typeof requiredValue === 'string' && requiredValue !== this.value) {
      return ` ${requiredValue}`;
    }
    else {
      return null;
    }
  }

  /**
   * Расхождение для числового типа (numeric): предложенное поставщиком число
   * должно попадать в требуемый покупателем диапазон [start; end].
   * Диапазоны считаем закрытыми либо пустыми.
   */
  getNumericMismatch(productCharacteristic: ProductCharacteristic): string | null {
    /** Требуемый диапазон из потребности */
    const requiredRange = productCharacteristic.value as NumericRange;

    /** Требований нет (пустой диапазон) — расхождения быть не может */
    if (requiredRange.isEmpty()) {
      return null;
    }

    /** Предлагаемое поставщиком значение */
    const offerValue = this.value as number | null;

    /** Значение попадает в требуемый диапазон — расхождения нет */
    if (offerValue !== null && requiredRange.contains(offerValue)) {
      return null;
    }
    else {
      return ` ${requiredRange.toString({ unit: productCharacteristic.unit?.name })}`;
    }
  }

  /** Расхождение для одиночного выбора из списка (selectableSingle) */
  getSelectableSingleMismatch(productCharacteristic: ProductCharacteristic): string | null {
    /** Требуемое покупателем значение — массив допустимых вариантов */
    const requiredValue = productCharacteristic.value;

    /** Предлагаемое поставщиком значение — один выбранный вариант (объект) или null */
    const offerValue = this.value;

    /** Покупатель не указал требований — сравнивать не с чем, расхождения нет */
    if (!Utils.isNotEmptyArray(requiredValue)) {
      return null;
    }

    /** Поставщик вообще не выбрал значение — это расхождение */
    if (offerValue === null) {
      return ` ${this.formatRequiredValue(requiredValue)}`;
    }

    /** Предложенное значение должно присутствовать среди требуемых (сравнение по id) */
    const isOfferValueAllowed = requiredValue.some(
      (requiredItem: { id: number }) => requiredItem.id === (offerValue as { id: number }).id,
    );

    /** Если предложенного значения нет среди требуемых — расхождение */
    if (isOfferValueAllowed) {
      return null;
    }
    else {
      return ` ${this.formatRequiredValue(requiredValue)}`;
    }
  }

  /** Расхождение для множественного выбора из списка (selectableMulti) */
  getSelectableMultiMismatch(productCharacteristic: ProductCharacteristic): string | null {
    /** Требуемые покупателем значения — массив вариантов */
    const requiredValue = productCharacteristic.value;

    /** Предлагаемые поставщиком значения */
    const offerValue = this.value;

    /** Покупатель не указал требований — расхождения нет */
    if (!Utils.isNotEmptyArray(requiredValue)) {
      return null;
    }

    /** Поставщик вообще не выбрал значения — расхождение */
    if (offerValue === null) {
      return ` ${this.formatRequiredValue(requiredValue)}`;
    }

    /** Извлекает массив id из значения (массив, объект или пусто) */
    function extractIds(value: unknown): number[] {
      if (Array.isArray(value)) {
        return value.map(item => item.id);
      }
      else if (value && typeof value === 'object') {
        return [(value as { id: number }).id];
      }
      else {
        return [];
      }
    }

    /** Множества id: что требуется покупателем и что предложено поставщиком */
    const requiredIds = new Set(extractIds(requiredValue));
    const offerIds    = new Set(extractIds(offerValue));

    /** Требуемые значения, которых нет в предложении (теоретико-множественная разность) */
    const missingIds = requiredIds.difference(offerIds);

    let hasError = false;

    if (productCharacteristic.characteristic.selectableListFilterMode === Characteristic.SELECTABLE_LIST_FILTER_MODE_CONTAINMENT) {
      /** Вхождение: все требуемые значения должны быть в предложении → расхождение, если хоть одного не хватает */
      hasError = missingIds.size > 0;
    }
    else if (productCharacteristic.characteristic.selectableListFilterMode === Characteristic.SELECTABLE_LIST_FILTER_MODE_INTERSECTION) {
      /** Пересечение: достаточно хотя бы одного совпадения → расхождение, если не совпало ничего */
      hasError = missingIds.size === requiredIds.size;
    }

    if (hasError) {
      return ` ${this.formatRequiredValue(requiredValue)}`;
    }
    else {
      return null;
    }
  }

  /** Форматирует список требуемых значений в строку через запятую */
  private formatRequiredValue(requiredValue: Array<{ name: string }>): string {
    return requiredValue.map(item => item.name).join(', ');
  }
}
